import { Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { UploadDocumentProvider } from "@/contexts/UploadDocumentContext";
import { UploadDocumentDialog, GlobalDragDropOverlay } from "@/components/upload";
import { ChatPanel } from "@/components/ui/chat-panel";
import { FloatingPromptBarProvider } from "@/components/ui/floating-prompt-bar-context";
import { cn } from "@/utils/Functions";
import { ChatPanelProvider, useChatPanel } from "@/contexts/ChatPanelContext";
import { GuidedFlowLayoutChatProvider, useGuidedFlowLayoutChat } from "@/features/guided-flow/contexts/GuidedFlowLayoutChatContext";
import { PortfolioProvider } from "@/contexts/PortfolioContext";
import { CreatePortfolioDialog } from "@/components/portfolio/CreatePortfolioDialog";
import { useIsMobile } from "@/hooks/common/useMobile";
import { useInboxStreamProvider } from "@/hooks/inbox/useInboxStreamProvider";


// Inner component that uses the chat panel context

const AppLayoutWithSidebarContent = () => {
  const location = useLocation();
  const isInteractiveDemo = location.pathname === '/demo/interactive';
  const isNewDealForm = location.pathname === '/demo/new-deal' || location.pathname === '/new-deal';
  // The inbox has its own reading-pane Ari ask-bar, so the global ChatPanel dock
  // is redundant there. Hide it on BOTH the demo and the real inbox (was only
  // matching '/demo/inbox', which left the real /inbox with a duplicate Ari bar).
  const isInboxRoute = location.pathname === '/demo/inbox' || location.pathname === '/inbox';
  const isDemo = location.pathname.startsWith('/demo');
  const isMobile = useIsMobile();
  const { layoutMode, isOpen, closePanel } = useChatPanel();

  // App-shell-level SINGLE OWNER of the Nylas inbox SSE stream. Opening it
  // here (rather than inside the /inbox page) keeps the EventSource alive
  // across navigation within this shell instance so the inbox SWR cache stays
  // warm. Route-authoritative isDemo gates it off on the demo shell.
  useInboxStreamProvider({ isDemo });

  // Hide chat panel on specific pages (chat page has its own full-screen chat)
  const isChatPage =
    location.pathname === '/demo/chat'
    || location.pathname === '/chat'
    || location.pathname === '/demo/create'
    || location.pathname === '/create';
  const hideChatPanel = isInteractiveDemo || isNewDealForm || isChatPage || isInboxRoute;
  const shouldReserveDockSpace = !hideChatPanel && layoutMode === "dock" && !isMobile;
  const shouldReserveSidebarSpace = !hideChatPanel && layoutMode === "sidebar" && isOpen && !isMobile;
  const { demoMessages, demoAction, demoLoading } = useGuidedFlowLayoutChat();

  useEffect(() => {
    if (hideChatPanel) {
      closePanel();
    }
  }, [hideChatPanel, closePanel]);

  return (
    <>
      <AppSidebar isDemo={isDemo} />
      <main 
        className={cn(
          "h-svh flex-1 min-w-0 transition-[padding-bottom] duration-300 ease-out p-2 pl-0 overflow-hidden bg-sidebar",
        )}
        style={{
          // paddingBottom: shouldReserveDockSpace ? "var(--chat-bottom-space, 0px)" : undefined,
          paddingRight: shouldReserveSidebarSpace ? "var(--app-chat-sidebar-width, 0px)" : undefined,
        }}
      >
        <div className={cn("bg-background h-full w-full overflow-y-auto border border-border rounded-xl shadow-sm transition-all duration-300 ease-out-expo", {
          "": !shouldReserveDockSpace,
          "h-[calc(100vh-5rem)]": shouldReserveDockSpace && !isOpen,
        })}>
        <Outlet />
        </div>
      </main>
      <UploadDocumentDialog />
      <CreatePortfolioDialog />
      <GlobalDragDropOverlay />
      {/* Hide chat panel on interactive demo and form pages */}
      {!hideChatPanel && (
        <ChatPanel
          demoMessages={demoMessages}
          demoAction={demoAction}
          demoLoading={demoLoading}
        />
      )}
    </>
  );
};

// Layout for authenticated pages with sidebar
export const AppLayoutWithSidebar = () => {
  return (
    <SidebarProvider>
      <FloatingPromptBarProvider>
        <UploadDocumentProvider>
          <PortfolioProvider>
            <ChatPanelProvider>
              <GuidedFlowLayoutChatProvider>
                <AppLayoutWithSidebarContent />
              </GuidedFlowLayoutChatProvider>
            </ChatPanelProvider>
          </PortfolioProvider>
        </UploadDocumentProvider>
      </FloatingPromptBarProvider>
    </SidebarProvider>
  );
};

// Layout for auth/public pages without sidebar
export const AuthLayout = () => {
  return <Outlet />;
};

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

export default AppLayout;
